import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Lock, LogOut, Trash2, ChevronRight, Mail, Phone,
  Globe, MessageCircle, Calendar, Users, CheckCircle2, Clock, XCircle, RefreshCw,
  MapPin, Briefcase, Bell, FileText,
} from "lucide-react";
import { LogoMark } from "../components/Logo";
import { useAdminLeads } from "../hooks/useAdminLeads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rt_admin_token";

const STATUS_OPTIONS = [
  { value: "new", label: "New", className: "status-sent" },
  { value: "contacted", label: "Contacted", className: "status-draft" },
  { value: "converted", label: "Converted", className: "status-paid" },
  { value: "lost", label: "Lost", className: "status-cancelled" },
];

const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, response.data.token);
      setToken(response.data.token);
      toast.success("Welcome in.");
      setPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="admin-login-page">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <LogoMark size={36} />
            <span className="font-['Outfit'] text-xl font-bold tracking-tight text-slate-900 leading-none">ReadyTrades</span>
          </Link>

          <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-[0_8px_30px_rgb(15,23,42,0.05)]">
            <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-5">
              <Lock size={24} className="text-emerald-600" />
            </div>
            <h1 className="font-['Outfit'] text-3xl font-bold text-slate-900 mb-2" data-testid="admin-login-title">Admin access</h1>
            <p className="text-slate-500 text-sm mb-6">Enter your admin password to view leads.</p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-slate-700 text-sm font-medium">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="form-input mt-2"
                  autoFocus
                  data-testid="admin-password-input"
                />
              </div>
              <button type="submit" disabled={loggingIn} className="btn-primary w-full text-base py-3" data-testid="admin-login-btn">
                {loggingIn && <Loader2 className="animate-spin mr-2" size={18} />}
                {loggingIn ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>

          <Link to="/" className="block text-center text-xs text-slate-400 mt-6 hover:text-slate-600">← Back to readytrades.co.uk</Link>
        </div>
      </div>
    );
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

const AdminDashboard = ({ token, onLogout }) => {
  const { leads, stats, loading, refreshing, fetchLeads, updateStatus, deleteLead } = useAdminLeads(token, onLogout);
  const [filter, setFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);

  const handleStatusChange = async (leadId, status) => {
    const result = await updateStatus(leadId, status);
    if (result && selectedLead?.id === leadId) {
      setSelectedLead({ ...selectedLead, status });
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    const ok = await deleteLead(leadId);
    if (ok && selectedLead?.id === leadId) setSelectedLead(null);
  };

  const filteredLeads = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={28} />
            <div className="flex items-baseline gap-2">
              <span className="font-['Outfit'] text-lg font-bold text-slate-900">ReadyTrades</span>
              <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Admin</span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchLeads(true)} disabled={refreshing} className="p-2 text-slate-500 hover:text-slate-900 transition-colors" data-testid="refresh-btn" aria-label="Refresh">
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button onClick={onLogout} className="btn-ghost text-sm text-slate-600 hover:text-slate-900" data-testid="admin-logout-btn">
              <LogOut size={16} className="mr-1" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-5 lg:p-8">
        <div className="mb-8">
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900">Leads</h1>
          <p className="text-slate-500 text-sm">Manage submissions from your get-started form.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-8">
          <StatCard icon={Users} label="Total" value={stats.total} tone="slate" testid="stat-total" onClick={() => setFilter("all")} active={filter === "all"} />
          <StatCard icon={Clock} label="New" value={stats.new} tone="sky" testid="stat-new" onClick={() => setFilter("new")} active={filter === "new"} />
          <StatCard icon={MessageCircle} label="Contacted" value={stats.contacted} tone="amber" testid="stat-contacted" onClick={() => setFilter("contacted")} active={filter === "contacted"} />
          <StatCard icon={CheckCircle2} label="Converted" value={stats.converted} tone="emerald" testid="stat-converted" onClick={() => setFilter("converted")} active={filter === "converted"} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white border border-slate-100 rounded-3xl p-16 text-center">
            <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail size={24} className="text-slate-400" />
            </div>
            <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-1">No leads {filter !== "all" ? `marked "${filter}"` : "yet"}</h2>
            <p className="text-slate-500 text-sm">
              {filter === "all" ? "When someone submits the get-started form, it'll show up here." : "Try a different filter."}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Lead</th>
                    <th className="px-5 py-3 hidden md:table-cell">Trade</th>
                    <th className="px-5 py-3 hidden lg:table-cell">Contact</th>
                    <th className="px-5 py-3 hidden xl:table-cell">Submitted</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-5 py-4">
                        <button onClick={() => setSelectedLead(lead)} className="text-left">
                          <p className="font-semibold text-slate-900">{lead.name}</p>
                          <p className="text-xs text-slate-500">{lead.business_name}</p>
                        </button>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">{lead.trade_type}</span>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell text-xs text-slate-600">
                        <a href={`tel:${lead.phone}`} className="block hover:text-emerald-600">{lead.phone}</a>
                        <a href={`mailto:${lead.email}`} className="block text-slate-400 hover:text-emerald-600 truncate max-w-[200px]">{lead.email}</a>
                      </td>
                      <td className="px-5 py-4 hidden xl:table-cell text-xs text-slate-400">{formatDate(lead.created_at)}</td>
                      <td className="px-5 py-4">
                        <StatusBadge status={lead.status} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => setSelectedLead(lead)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700" data-testid={`view-lead-${lead.id}`}>
                          View <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

const TONE = {
  slate: "bg-slate-100 text-slate-700",
  sky: "bg-sky-100 text-sky-700",
  emerald: "bg-emerald-100 text-emerald-700",
  amber: "bg-amber-100 text-amber-700",
};

const StatCard = ({ icon: Icon, label, value, tone, testid, onClick, active }) => (
  <button
    onClick={onClick}
    className={`bg-white border rounded-2xl p-4 lg:p-5 text-left transition-all ${active ? "border-emerald-300 shadow-sm" : "border-slate-100 hover:border-slate-200"}`}
    data-testid={testid}
  >
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${TONE[tone]}`}>
      <Icon size={16} />
    </div>
    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{label}</p>
    <p className="font-['Outfit'] text-xl lg:text-2xl font-bold text-slate-900">{value}</p>
  </button>
);

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return <span className={`inline-block text-[10px] font-semibold uppercase px-2.5 py-1 rounded-full ${opt.className}`}>{opt.label}</span>;
};

const LeadDrawer = ({ lead, onClose, onStatusChange, onDelete }) => {
  const whatsappLink = `https://wa.me/${(lead.phone || "").replace(/[^\d]/g, "").replace(/^0/, "44")}?text=${encodeURIComponent(`Hi ${lead.name.split(" ")[0]}, this is ReadyTrades — thanks for getting in touch about ${lead.business_name}.`)}`;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto" data-testid="lead-drawer">
        <header className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">{lead.name}</h3>
            <p className="text-xs text-slate-500">{lead.business_name}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-500 hover:text-slate-900" data-testid="drawer-close-btn">
            <XCircle size={20} />
          </button>
        </header>

        <div className="p-6 space-y-6">
          <section>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick actions</p>
            <div className="grid grid-cols-3 gap-2">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-2xl p-3 transition-colors" data-testid="action-whatsapp">
                <MessageCircle size={18} />
                <span className="text-[11px] font-semibold">WhatsApp</span>
              </a>
              <a href={`tel:${lead.phone}`} className="flex flex-col items-center gap-1.5 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded-2xl p-3 transition-colors" data-testid="action-call">
                <Phone size={18} />
                <span className="text-[11px] font-semibold">Call</span>
              </a>
              <a href={`mailto:${lead.email}`} className="flex flex-col items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl p-3 transition-colors" data-testid="action-email">
                <Mail size={18} />
                <span className="text-[11px] font-semibold">Email</span>
              </a>
            </div>
          </section>

          <section>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Details</p>
            <dl className="space-y-3 text-sm">
              <DetailRow icon={Users} label="Trade" value={lead.trade_type} />
              {lead.business_stage && <DetailRow icon={Briefcase} label="Business stage" value={formatBusinessStage(lead.business_stage)} />}
              {lead.location && <DetailRow icon={MapPin} label="Location" value={lead.location} />}
              <DetailRow icon={Phone} label="Phone" value={lead.phone} href={`tel:${lead.phone}`} />
              <DetailRow icon={Mail} label="Email" value={lead.email} href={`mailto:${lead.email}`} />
              {lead.preferred_contact && <DetailRow icon={Bell} label="Prefers contact by" value={formatPreferredContact(lead.preferred_contact)} />}
              {lead.current_website && (
                <DetailRow icon={Globe} label="Current website" value={lead.current_website} href={lead.current_website.startsWith("http") ? lead.current_website : `https://${lead.current_website}`} external />
              )}
              {lead.social_media && <DetailRow icon={MessageCircle} label="Social media" value={lead.social_media} />}
              <DetailRow icon={Calendar} label="Submitted" value={formatDate(lead.created_at)} />
            </dl>
          </section>

          {lead.description && (
            <section data-testid="lead-description-section">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">What they told us</p>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText size={14} className="text-slate-500" />
                </div>
                <p className="flex-1 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap break-words bg-slate-50 border border-slate-100 rounded-xl p-3">
                  {lead.description}
                </p>
              </div>
            </section>
          )}

          <section>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Status</p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onStatusChange(lead.id, opt.value)}
                  className={`px-3 py-2.5 rounded-xl text-xs font-semibold transition-all border ${
                    lead.status === opt.value
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                  data-testid={`set-status-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>

          <section className="border-t border-slate-100 pt-5">
            <button
              onClick={() => onDelete(lead.id)}
              className="w-full border border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700 rounded-md py-2.5 text-sm font-medium flex items-center justify-center gap-2"
              data-testid="delete-lead-btn"
            >
              <Trash2 size={14} /> Delete lead
            </button>
          </section>
        </div>
      </aside>
    </>
  );
};

const DetailRow = ({ icon: Icon, label, value, href, external }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
      <Icon size={14} className="text-slate-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      {href ? (
        <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="text-slate-900 font-medium hover:text-emerald-600 transition-colors break-all">
          {value}
        </a>
      ) : (
        <p className="text-slate-900 font-medium break-words">{value}</p>
      )}
    </div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// GetStartedPage now posts human-readable labels ("Just starting out", "WhatsApp") so we
// render straight through. Keep these as identity helpers in case older leads exist that
// stored slug values.
const formatBusinessStage = (v) => v || "";
const formatPreferredContact = (v) => v || "";

export default AdminPage;
